import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import TalentService from '../../../services/talentService';

const TalentSection = ({ selectedTalentId, onTalentChange, className = "" }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsDropdownOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search talent profiles
  const searchTalent = async (term) => {
    if (!term?.trim()) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await TalentService?.getAllTalentProfiles(term?.trim());
      
      if (error) {
        throw error;
      }

      setSearchResults(data || []);
      setIsDropdownOpen(true);
    } catch (err) {
      console.error('Error searching talent:', err);
      setError('Failed to search talent profiles');
      setSearchResults([]);
      setIsDropdownOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchTalent(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleTalentSelect = (talent) => {
    setSelectedTalent(talent);
    setSearchTerm(`${talent?.first_name} ${talent?.last_name}`?.trim());
    setIsDropdownOpen(false);
    onTalentChange?.(talent?.id, talent);
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setSearchTerm(value);
    
    if (!value?.trim()) {
      setSelectedTalent(null);
      onTalentChange?.(null, null);
    }
  };

  const handleAddNewTalent = () => {
    const talentName = searchTerm?.trim();
    if (!talentName) return;

    // Create a temporary talent object for new talent suggestion
    const newTalent = {
      id: 'new-talent',
      first_name: talentName?.split(' ')?.[0] || talentName,
      last_name: talentName?.split(' ')?.slice(1)?.join(' ') || '',
      display_name: talentName,
      email: '',
      isNew: true
    };

    setSelectedTalent(newTalent);
    setIsDropdownOpen(false);
    onTalentChange?.('new-talent', newTalent);
  };

  return (
    <div className={`bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 ${className}`}>
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
          <Icon name="User" size={18} className="text-blue-200" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-white">Talent</h2>
      </div>

      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => {
              if (searchTerm?.trim() && searchResults?.length > 0) {
                setIsDropdownOpen(true);
              }
            }}
            placeholder="Select Performing Talent"
            className="w-full px-4 py-3 pr-10 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
          
          {/* Search icon or loading spinner */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {loading ? (
              <LoadingSpinner size="sm" className="text-blue-200" />
            ) : (
              <Icon name="Search" size={18} className="text-blue-200" />
            )}
          </div>
        </div>

        {/* Dropdown */}
        {isDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white bg-opacity-95 backdrop-blur-sm border border-white border-opacity-30 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {error ? (
              <div className="p-3 text-sm text-red-600 bg-red-50 bg-opacity-90 m-2 rounded-lg">
                {error}
              </div>
            ) : searchResults?.length > 0 ? (
              <div className="py-2">
                {searchResults?.map((talent) => (
                  <button
                    key={talent?.id}
                    onClick={() => handleTalentSelect(talent)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 hover:bg-opacity-50 transition-colors duration-150"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {talent?.first_name} {talent?.last_name}
                        </div>
                        {talent?.display_name && talent?.display_name !== `${talent?.first_name} ${talent?.last_name}` && (
                          <div className="text-sm text-gray-600">
                            Display Name: {talent?.display_name}
                          </div>
                        )}
                        {talent?.email && (
                          <div className="text-xs text-gray-500">
                            {talent?.email}
                          </div>
                        )}
                      </div>
                      <Icon name="User" size={16} className="text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            ) : searchTerm?.trim() ? (
              <div className="p-4 text-center">
                <div className="text-sm text-gray-600 mb-3">
                  No matching talent found for "{searchTerm}"
                </div>
                <button
                  onClick={handleAddNewTalent}
                  className="inline-flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors duration-150"
                >
                  <Icon name="Plus" size={14} />
                  <span>Add "{searchTerm}" as new talent</span>
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Selected talent display */}
      {selectedTalent && (
        <div className="mt-4 p-3 bg-blue-50 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name={selectedTalent?.isNew ? "Plus" : "CheckCircle"} size={16} className="text-blue-300" />
              <div>
                <div className="text-sm font-medium text-blue-100">
                  {selectedTalent?.isNew ? 'New Talent:' : 'Selected Talent:'}
                </div>
                <div className="text-white">
                  {selectedTalent?.first_name} {selectedTalent?.last_name}
                </div>
                {selectedTalent?.isNew && (
                  <div className="text-xs text-blue-200 mt-1">
                    This talent will be created when you save the song
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedTalent(null);
                setSearchTerm('');
                onTalentChange?.(null, null);
              }}
              className="text-blue-200 hover:text-white transition-colors duration-150"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Helper text */}
      <div className="mt-4 text-sm text-blue-200">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="mb-1">Start typing a name to search existing talent profiles.</p>
            <p>If no match is found, you can add them as new talent.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentSection;